import * as React from 'react';
import {PieChart, pieArcLabelClasses} from '@mui/x-charts/PieChart';
import {useDrawingArea} from '@mui/x-charts/hooks';
import {styled} from '@mui/material/styles';
import {ReactComponent as PieChartPointer} from '../../../assests/svg/pie-chart-pointer.svg';
import {ReactComponent as ButtonIcon} from '../../../assests/svg/chev_right_blue.svg';
import {t} from 'i18next';

const StyledText = styled('text')(({theme}) => ({
  fill: theme.palette.text.primary,
  dominantBaseline: 'central',
  fontSize: 20,
}));

const ARROW_PADDING_X = 12; //Spacing between arrow and text
const ARROW_WIDTH = 21;
const ARROW_HEIGHT = 11;
const FONT_WIDTH = 7;
const LINE_HEIGHT = 20;
const BUTTON_HEIGHT = 32;
const BUTTON_WIDTH = 32;

const AurPieChartCenterLabel = ({children}: {children: React.ReactNode}) => {
  const {height, width, left, top} = useDrawingArea();
  return (
    <StyledText
      className="aur-pie-chart-center-label"
      textAnchor="middle"
      x={left + width / 2}
      y={top + height / 2}
    >
      {children}
    </StyledText>
  );
};

interface AurPieChartLabelProps {
  index: number;
  data: {
    value: number;
    label: string;
    onClick?: () => void;
  }[];
  radius: number;
}

const wrapSVGText = (
  text: string,
  maxLength: number
): {text: string[]; length: number} => {
  if (text.length < maxLength) return {text: [text], length: text.length};
  else {
    const words = text.split(' ');
    const lines: string[] = [];
    let length = 0;
    for (let i = 0; i < words.length; i++) {
      if (words[i].length >= maxLength) {
        lines.push(words[i]);
        if (words[i].length > length) length = words[i].length;
        continue;
      }
      let currentLine = words[i];
      while (currentLine.length + 1 <= maxLength) {
        if (i === words.length - 1) break;

        if (currentLine.length + words[i + 1].length + 1 <= maxLength) {
          i++;
          currentLine += ' ' + words[i];
        } else break;
      }
      lines.push(currentLine);
      if (currentLine.length > length) length = currentLine.length;
    }
    return {text: lines, length: length};
  }
};

const calcLabelOffset = (
  totalValue: number,
  previousTotal: number,
  currentValue: number,
  radius: number
): {x: number; y: number} => {
  const arcCenter = previousTotal + currentValue / 2;
  const arcAngle = (arcCenter / totalValue) * 2 * Math.PI; //Angle of center of this arc segment, in radians
  return {
    x: Math.sin(arcAngle) * radius, //Horiztonal Offset
    y: Math.cos(arcAngle) * radius, //Vertical Offset
  };
};

const AurPieChartLabel = (props: AurPieChartLabelProps) => {
  const {height, width, left, top} = useDrawingArea();
  const {index, data, radius} = props;
  const xOrigin = left + width / 2;
  const yOrigin = top + height / 2;
  let xCoord = 0;
  let yCoord = 0;
  let textXCoord = 0;
  let maxWidth = 0;
  let arrowCssClasses = '';

  const previousTotal = data
    .filter((_, i) => index > i)
    .reduce((n, {value}) => n + value, 0);
  const dataTotal = data.reduce((n, {value}) => n + value, 0);

  const offset = calcLabelOffset(
    dataTotal,
    previousTotal,
    data[index].value,
    radius
  );
  yCoord = yOrigin - offset.y;
  xCoord = offset.x + xOrigin;

  if (xCoord > xOrigin) {
    textXCoord = xCoord + ARROW_PADDING_X + ARROW_WIDTH;
    maxWidth = width - xCoord - ARROW_PADDING_X - ARROW_WIDTH;
  } else {
    textXCoord = xCoord - ARROW_PADDING_X - ARROW_WIDTH;
    arrowCssClasses += 'flip-arrow-x';
    maxWidth = xCoord - ARROW_PADDING_X - ARROW_WIDTH - ARROW_PADDING_X;
  }

  if (yCoord > yOrigin) {
    if (arrowCssClasses.length > 0) arrowCssClasses = 'flip-arrow-both';
    else arrowCssClasses += 'flip-arrow-y';
  }

  if (data[index].onClick) maxWidth -= BUTTON_WIDTH + ARROW_PADDING_X;

  const lines = wrapSVGText(data[index].label, maxWidth / FONT_WIDTH);

  return xCoord > xOrigin ? (
    <>
      <PieChartPointer
        x={xCoord}
        y={yCoord < yOrigin ? yCoord : yCoord - ARROW_HEIGHT}
        className={arrowCssClasses}
      />
      <StyledText
        textAnchor="start"
        x={textXCoord}
        y={yCoord}
        className={
          data[index].onClick ? 'aur-pie-label aur-pie-link' : 'aur-pie-label'
        }
      >
        {lines.text.map((item, line) => {
          return (
            <tspan x={textXCoord} dy={line === 0 ? '0px' : `${LINE_HEIGHT}px`}>
              {item}
            </tspan>
          );
        })}
        <tspan x={textXCoord} dy={`20px`}>
          {data[index].value.toFixed(1)}{' '}
          {t('reports.hostingVolume.cumulativeReport.gb')}
        </tspan>
      </StyledText>
      {data[index].onClick ? (
        <ButtonIcon
          className="aur-pie-link-button"
          x={textXCoord + lines.length * FONT_WIDTH + ARROW_PADDING_X}
          y={yCoord - 8}
          onClick={data[index].onClick}
        />
      ) : (
        <></>
      )}
    </>
  ) : (
    <>
      <PieChartPointer
        x={xCoord - ARROW_WIDTH}
        y={yCoord < yOrigin ? yCoord : yCoord - ARROW_HEIGHT}
        className={arrowCssClasses}
      />
      <StyledText
        textAnchor="start"
        x={textXCoord}
        y={yCoord}
        className={
          data[index].onClick ? 'aur-pie-label aur-pie-link' : 'aur-pie-label'
        }
      >
        {lines.text.map((item, line) => {
          return (
            <tspan
              x={
                data[index].onClick
                  ? textXCoord -
                    lines.length * FONT_WIDTH -
                    BUTTON_WIDTH -
                    ARROW_PADDING_X / 2
                  : textXCoord - lines.length * FONT_WIDTH
              }
              dy={line === 0 ? '0px' : `20px`}
            >
              {item}
            </tspan>
          );
        })}
        <tspan
          x={
            data[index].onClick
              ? textXCoord -
                lines.length * FONT_WIDTH -
                BUTTON_WIDTH -
                ARROW_PADDING_X / 2
              : textXCoord - lines.length * FONT_WIDTH
          }
          dy={`${LINE_HEIGHT}px`}
        >
          {data[index].value.toFixed(1)}{' '}
          {t('reports.hostingVolume.cumulativeReport.gb')}
        </tspan>
      </StyledText>
      {data[index].onClick ? (
        <ButtonIcon
          className="aur-pie-link-button"
          x={textXCoord - BUTTON_WIDTH + ARROW_PADDING_X / 2}
          y={yCoord - BUTTON_HEIGHT / 2 + ARROW_HEIGHT / 2}
          onClick={data[index].onClick}
        />
      ) : (
        <></>
      )}
    </>
  );
};

interface AurPieChartProps {
  radius: number;
  innerRadius?: number;
  size: {
    height: number;
    width: number;
  };
  centerText?: React.ReactNode;
  data: {
    value: number;
    label: string;
    button?: undefined;
  }[];
  labelPadding?: number;
}

const AurPieChart = (props: AurPieChartProps) => {
  const {
    data,
    size,
    centerText,
    radius,
    innerRadius = 0,
    labelPadding = 0,
  } = props;

  return (
    <div>
      <PieChart
        className="pie-chart-no-legend"
        margin={{right: 0}}
        series={[{data: data, outerRadius: radius, innerRadius: innerRadius}]}
        sx={{
          [`& .${pieArcLabelClasses.root}`]: {
            fontWeight: '400',
            maxWidth: '150px',
            textWrap: 'wrap',
            fontFamily: 'Inter',
            fontSize: '14px',
            lineHeight: '20px',
          },
        }}
        slotProps={{
          legend: {hidden: true},
        }}
        {...size}
      >
        {data.map((_, index) => {
          return (
            <AurPieChartLabel
              key={index}
              index={index}
              data={data}
              radius={radius + labelPadding}
            />
          );
        })}
        {centerText ? (
          <AurPieChartCenterLabel>{centerText}</AurPieChartCenterLabel>
        ) : (
          <></>
        )}
      </PieChart>
    </div>
  );
};

export default AurPieChart;
